/* eslint-disable react/no-array-index-key */

import { MouseEvent, ReactNode } from "react";
import { Descendant } from "slate";

import {
  PlaceholderData,
  isEditorElementType,
  isEditorText,
  ParagraphTag,
  EditorElement,
} from "@smart/itops-serialisation-basic";

import { DisplayLink } from "./display-link";
import { DisplayParagraph } from "./display-paragraph";

type ChildrenProps = {
  paragraphTag?: ParagraphTag;
  hintElement?: ReactNode;
  items: Descendant[];
  placeholderData?: Partial<PlaceholderData>;
  onPlaceholderClick?: (
    e: MouseEvent<HTMLSpanElement>,
    element: EditorElement<"placeholder">,
  ) => void;
};

const Children = ({
  paragraphTag,
  hintElement,
  items,
  placeholderData,
  onPlaceholderClick,
}: ChildrenProps) => (
  <>
    {items.map((it, index) => {
      if (isEditorElementType("paragraph")(it)) {
        return (
          <DisplayParagraph Tag={paragraphTag} key={index}>
            <Children
              items={it.children}
              placeholderData={placeholderData}
              onPlaceholderClick={onPlaceholderClick}
            />
            {index === 0 && hintElement}
          </DisplayParagraph>
        );
      }

      if (isEditorElementType("link")(it)) {
        return (
          <DisplayLink element={it} key={index}>
            <Children
              items={it.children}
              placeholderData={placeholderData}
              onPlaceholderClick={onPlaceholderClick}
            />
          </DisplayLink>
        );
      }

      if (isEditorElementType("placeholder")(it)) {
        return it.isLink ? (
          <a
            href={placeholderData?.[it.name] || it.name}
            target="_blank"
            rel="noreferrer"
            className="underline text-blue-360 text-paragraph"
            onClick={(e) => onPlaceholderClick?.(e, it)}
          >
            {it.displayText || it.name}
          </a>
        ) : (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <span key={index} onClick={(e) => onPlaceholderClick?.(e, it)}>
            {placeholderData?.[it.name]}
          </span>
        );
      }

      if (isEditorText(it)) {
        let display: string | JSX.Element = it.text;
        if (it.bold) display = <strong>{display}</strong>;
        if (it.italic) display = <em>{display}</em>;

        return <span key={index}>{display}</span>;
      }
      return null;
    })}
  </>
);

export { Children, ChildrenProps };
